import React from 'react';

import { LicenseInfo } from '@mui/x-license-pro';
import { GoogleOAuthProvider } from '@react-oauth/google';
import type { AxiosError, AxiosResponse } from 'axios';
import ReactDOM from 'react-dom/client';
import { toast } from 'sonner';

import App from './App.tsx';
import { setup } from './services/config.ts';
import { errorReporting } from './services/logging.ts';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

setup({
  domains: {
    auth: `${import.meta.env.VITE_AUTH_URL}${import.meta.env.VITE_AUTH_API_PATH}`,
    members: `${import.meta.env.VITE_MEMBERS_URL}${import.meta.env.VITE_MEMBERS_API_PATH}`,
    plans: `${import.meta.env.VITE_PLANS_URL}${import.meta.env.VITE_PLANS_API_PATH}`,
    reports: `${import.meta.env.VITE_REPORTS_URL}${import.meta.env.VITE_REPORTS_API_PATH}`,
    membersV2: `${import.meta.env.VITE_MEMBERS_URL}${import.meta.env.VITE_MEMBERS_V2_API_PATH}`,
  },
  paths: {
    auth: `${import.meta.env.VITE_AUTH_API_PATH}`,
    members: `${import.meta.env.VITE_MEMBERS_API_PATH}`,
    plans: `${import.meta.env.VITE_PLANS_API_PATH}`,
    reports: `${import.meta.env.VITE_REPORTS_API_PATH}`,
  },
  queryClientOptions: {
    queries: {
      refetchOnMount: true,
      retry: 0,
      staleTime: 1000 * 60 * 2,
    },
    mutations: {
      onSuccess: (response: any) => {
        toast.success((response as AxiosResponse<{ message: string }>).data.message);
      },
      onError: (error: any) => {
        const msg = (error as AxiosError<{ message: string }>).response?.data.message;
        if (msg && error.status && !['417'].includes(error.status.toString())) {
          toast.error(msg);
        }
      },
    },
  },
  getToken: async () =>
    new Promise((resolve) => {
      const impToken = sessionStorage.getItem('impersonated_token');
      const accessToken = localStorage.getItem('access_token');

      if (impToken) return resolve(impToken);
      if (accessToken) return resolve(accessToken);
      resolve(null);
    }),
  setToken: async (key, token) => {
    if (key === 'access_token') {
      localStorage.setItem('access_token', token);
    }
    if (key === 'impersonated_token') {
      sessionStorage.setItem('impersonated_token', token);
    }
    return Promise.resolve();
  },
  clearToken: async () => {
    if (sessionStorage.getItem('impersonated_token')) {
      sessionStorage.removeItem('impersonated_token');
    }
    if (localStorage.getItem('access_token')) {
      localStorage.removeItem('access_token');
    }
    return Promise.resolve();
  },
});

errorReporting.init();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      <App />
    </GoogleOAuthProvider>
  </React.StrictMode>,
);
